import { err, ok, Result } from "neverthrow";
import GraphqlApiError from "@/framework/network/gql/GraphqlApiError";
import {
  AsyncTranslateTextDocument,
  AsyncTranslateTextQuery,
  DictionaryTarget,
  ExportDictDocument,
  ExportDictMutation,
  GetClientDictionaryDocument,
  GetClientDictionaryQuery,
  GetExportDictStatusDocument,
  GetExportDictStatusQuery,
  GetSubMenuDictRelationDocument,
  GetSubMenuDictRelationQuery,
  ImportProcessDocument,
  ImportProcessMutation,
  ImportSheetDocument,
  ImportSheetMutation,
  ListUserDictDocument,
  ListUserDictQuery,
  ProcessImportDocument,
  ProcessImportMutation,
  ReTranslateOriginalQcBizDictsInTenantDocument,
  ReTranslateOriginalQcBizDictsInTenantMutation,
  SaveClientDictionaryDocument,
  SaveClientDictionaryMutation,
  SaveUserDictListDocument,
  SaveUserDictListMutation,
} from "@/framework/network/gql/generated/graphql";
import {
  GetSubMenuBotDictRelationDocument,
  GetSubMenuBotDictRelationQuery,
  ListBotDictDocument,
  ListBotDictQuery,
  ReTranslateContentsDictsDocument,
  ReTranslateContentsDictsMutation,
  SaveBotDictListDocument,
  SaveBotDictListMutation,
} from "@/framework/network/gql/generated/graphql-bot";
import { UserDictList, UserDictListSchema } from "@/modules/admin/objects/hotelAdmin";
import { GraphqlApi, IGraphqlApi } from "@/framework/network/gql/GraphqlApi";
import { GeneralResponse, GeneralResponseSchema } from "@/framework/objects/GeneralResponse";
import { DICT_TYPE } from "@/modules/admin/services/hotelAdmin";
import { i18n } from "@/framework/plugins/i18n";
import { AsyncTranslationResult, AsyncTranslationResultSchema } from "@/framework/types/AsyncTranslationResult";
import {
  Dictionary,
  DictionaryList,
  DictMessage,
  ExportDictResult,
  ExportDictResultSchema,
  ExportDictStatus,
  ExportDictStatusSchema,
  ImportProcessResult,
  ImportProcessResultSchema,
  ImportSheetResult,
  ImportSheetResultSchema,
  ProcessImportResult,
  ProcessImportResultSchema,
} from "@/modules/admin/objects/bizDicts";
import { SubMenuDictRelations, SubMenuDictRelationsSchema } from "@/modules/staff/objects/guest/SubMenu";
import { CustomFetchClientDictionary } from "@/framework/network/gql/custom/FetchClientDictionary/CustomFetchClientDictionary";

export class DictionaryService {
  private _graphql: IGraphqlApi;
  private _botGraphql: IGraphqlApi;

  public constructor(graphql: IGraphqlApi = new GraphqlApi(), botGraphql: IGraphqlApi = new GraphqlApi(true)) {
    this._graphql = graphql;
    this._botGraphql = botGraphql;
  }

  async reTranslateOriginalQcBizDicts(): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<
      ReTranslateOriginalQcBizDictsInTenantMutation,
      GeneralResponse
    >(ReTranslateOriginalQcBizDictsInTenantDocument, GeneralResponseSchema, {});
    if (res.isOk()) {
      return ok(res.value);
    } else {
      return err(res.error);
    }
  }

  async reTranslateOriginalContentDicts(
    tenantId: number,
    tenantBaseLang: string,
    dictCds: Array<string>
  ): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const res = await this._botGraphql.runMutationAndValidate<ReTranslateContentsDictsMutation, GeneralResponse>(
      ReTranslateContentsDictsDocument,
      GeneralResponseSchema,
      { tenantId, tenantBaseLang, dictCds }
    );
    if (res.isOk()) {
      return ok(res.value);
    } else {
      return err(res.error);
    }
  }

  async getExportDictState(tenantId: number, uniqueId: string): Promise<Result<ExportDictStatus, GraphqlApiError>> {
    const result = await this._graphql.runQueryAndValidate<GetExportDictStatusQuery, ExportDictStatus>(
      GetExportDictStatusDocument,
      ExportDictStatusSchema,
      { tenantId: tenantId, unique_id: uniqueId },
      true
    );
    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async getMenuDictRelation(tenantId: number): Promise<Result<SubMenuDictRelations, GraphqlApiError>> {
    const result = await this._graphql.runQueryAndValidate<GetSubMenuDictRelationQuery, SubMenuDictRelations>(
      GetSubMenuDictRelationDocument,
      SubMenuDictRelationsSchema,
      { tenantId: tenantId }
    );
    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async getMenuDictBotRelation(tenantId: number): Promise<Result<SubMenuDictRelations, GraphqlApiError>> {
    const result = await this._botGraphql.runQueryAndValidate<GetSubMenuBotDictRelationQuery, SubMenuDictRelations>(
      GetSubMenuBotDictRelationDocument,
      SubMenuDictRelationsSchema,
      { tenantId: tenantId }
    );
    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async importProcess(tenantId: number): Promise<Result<ImportProcessResult, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<ImportProcessMutation, ImportProcessResult>(
      ImportProcessDocument,
      ImportProcessResultSchema,
      { tenant_id: tenantId, lang_cd: i18n.locale }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async fetchClientDictionary(
    targetType: DictionaryTarget,
    conversationId: string,
    noCache: boolean
  ): Promise<Result<UserDictList, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<GetClientDictionaryQuery, UserDictList>(
      GetClientDictionaryDocument,
      UserDictListSchema,
      { targetType: targetType, conversationId: conversationId },
      noCache
    );

    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async checkDictState(
    targetType: DictionaryTarget,
    conversationId: string,
    noCache: boolean
  ): Promise<
    Result<
      {
        id: number;
        state: string;
        target_id: string;
        target_type: string;
      },
      GraphqlApiError
    >
  > {
    const res = await this._graphql.runQuery(CustomFetchClientDictionary, { targetType, conversationId }, noCache);
    return res.isErr() ? err(res.error) : ok(res.value.getClientDictionary);
  }

  loadUserDictionaries = async (
    dictType: DICT_TYPE,
    tenantId: number,
    filterText: string,
    page: number,
    limit: number,
    subMenuId?: number,
    noCache: boolean = false
  ): Promise<Result<DictionaryList, GraphqlApiError>> => {
    let response: UserDictList;
    let resp;
    let custom = false;
    if (dictType == DICT_TYPE.BOT) {
      resp = await this.fetchBotDictionary(tenantId, filterText, page, limit, subMenuId, noCache);
    } else if (dictType == DICT_TYPE.BIZ) {
      resp = await this.fetchUserDictionary(tenantId, filterText, page, limit, subMenuId, noCache);
    } else if (dictType == DICT_TYPE.CUSTOM) {
      resp = await this.fetchClientDictionary(DictionaryTarget.Tenant, "", noCache);
      custom = true;
    } else {
      return ok({ tenantId: tenantId, page: page, totalPage: 0, items: [] });
    }
    if (resp.isErr()) {
      return err(resp.error);
    }
    response = resp.value;
    if ((!response || !response.items) && custom) {
      return ok({ tenantId: tenantId, page: page, totalPage: 0, items: [] });
    }
    const dictionaries: Dictionary[] = response.items.map((item) => {
      const messages: DictMessage[] = item.messages.map((message) => {
        return {
          langCd: message.lang_cd,
          message: message.message,
          isAllowedToUpdate: message.is_allowed_to_update,
        };
      });

      return {
        dictCd: item.dict_cd,
        messages: messages,
      };
    });

    return ok({
      tenantId: response.tenant_id,
      page: response.page,
      totalPage: response.total_page,
      items: dictionaries,
    });
  };

  async fetchBotDictionary(
    tenantId: number,
    filterText: string,
    page: number,
    limit: number,
    sub_menu_id?: number,
    noCache?: boolean
  ): Promise<Result<UserDictList, GraphqlApiError>> {
    const res = await this._botGraphql.runQueryAndValidate<ListBotDictQuery, UserDictList>(
      // @ts-ignore conflict between generic typing and exact requirement
      ListBotDictDocument,
      UserDictListSchema,
      { tenantId: tenantId, filterText: filterText, page: page, limit: limit, sub_menu_id: sub_menu_id },
      noCache
    );

    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async fetchUserDictionary(
    tenantId: number,
    filterText: string,
    page: number,
    limit: number,
    sub_menu_id?: number,
    noCache?: boolean
  ): Promise<Result<UserDictList, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<ListUserDictQuery, UserDictList>(
      // @ts-ignore conflict between generic typing and exact requirement
      ListUserDictDocument,
      UserDictListSchema,
      {
        tenantId: tenantId,
        filterText: filterText,
        page: page,
        limit: limit,
        sub_menu_id: sub_menu_id ? sub_menu_id : null,
      },
      noCache
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async saveUserDictionaries(dictType: DICT_TYPE, tenantId: number, dictItems: Dictionary[]): Promise<string> {
    let items = dictItems.map((dictItem) => ({
      dict_cd: dictItem.dictCd,
      messages: dictItem.messages.map((message) => ({
        lang_cd: message.langCd,
        message: message.message,
        is_allowed_to_update: message.isAllowedToUpdate,
      })),
    }));

    let res;
    if (dictType == DICT_TYPE.BOT) {
      res = await this.saveBotDictionary(tenantId, items);
    } else if (dictType == DICT_TYPE.BIZ) {
      res = await this.saveUserDictionary(tenantId, items);
    } else if (dictType == DICT_TYPE.CUSTOM) {
      res = await this.saveCustomDictionary(DictionaryTarget.Tenant, "", items);
    } else {
      return "";
    }
    return res.isErr() ? "error" : (res && res.value.message) || "";
  }

  async saveUserDictionary(tenantId: number, items: any[]): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<SaveUserDictListMutation, GeneralResponse>(
      SaveUserDictListDocument,
      GeneralResponseSchema,
      { tenantId: tenantId, items: items }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async saveBotDictionary(tenantId: number, items: any[]): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const res = await this._botGraphql.runMutationAndValidate<SaveBotDictListMutation, GeneralResponse>(
      SaveBotDictListDocument,
      GeneralResponseSchema,
      { tenantId: tenantId, items: items }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async saveCustomDictionary(
    targetType: DictionaryTarget,
    conversationId: string,
    items: any[]
  ): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<SaveClientDictionaryMutation, GeneralResponse>(
      SaveClientDictionaryDocument,
      GeneralResponseSchema,
      { targetType: targetType, conversationId: conversationId, items: items }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async importDictSheet(): Promise<Result<ImportSheetResult, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<ImportSheetMutation, ImportSheetResult>(
      // @ts-ignore conflict between generic typing and exact requirement
      ImportSheetDocument,
      ImportSheetResultSchema,
      {}
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async processDictImport(
    tenant_id: number,
    key: string,
    target: string
  ): Promise<Result<ProcessImportResult, GraphqlApiError>> {
    const v = { tenant_id, key, target, lang_cd: i18n.locale };
    const res = await this._graphql.runMutationAndValidate<ProcessImportMutation, ProcessImportResult>(
      ProcessImportDocument,
      ProcessImportResultSchema,
      v
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async processDictExport(
    tenantId: number,
    dict_cds: string[],
    type: string
  ): Promise<Result<ExportDictResult, GraphqlApiError>> {
    const v = { tenantId, dict_cds, type };
    const res = await this._graphql.runMutationAndValidate<ExportDictMutation, ExportDictResult>(
      ExportDictDocument,
      ExportDictResultSchema,
      v
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async asyncTranslation(
    text: string,
    fromLang: string,
    toLang: string[],
    customDictName: string
  ): Promise<Result<AsyncTranslationResult, GraphqlApiError>> {
    const v = { text, fromLang, toLang, customDictName };
    const res = await this._graphql.runQueryAndValidate<AsyncTranslateTextQuery, AsyncTranslationResult>(
      AsyncTranslateTextDocument,
      AsyncTranslationResultSchema,
      v
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }
}
